import { AccountNumber } from '@/modules/projects/components/Workbench/AccountNumber'
import type { StatusFor } from '@/modules/projects/components/Workbench/coverage'
import { MapCell } from '@/modules/projects/components/Workbench/MapCell'
import { laneNumberOf } from '@/modules/projects/components/Workbench/map'
import {
  type BlockPiece,
  type BlockYear,
  gridRowOf,
} from '@/modules/projects/components/Workbench/mapBlocks'
import {
  GRID,
  TRACK_HEIGHT,
} from '@/modules/projects/components/Workbench/mapLayout'
import type {
  Highlight,
  MapActions,
} from '@/modules/projects/components/Workbench/mapView'
import { StitchedSpan } from '@/modules/projects/components/Workbench/StitchedSpan'
import { cn } from '@/utils'

export interface BlockYearRowsProps {
  row: BlockYear
  choosing: boolean
  openStatementId: string | null
  highlight: Highlight
  statusFor: StatusFor
  actions: MapActions
}

// Account rows sit far enough apart for the hairline joining a statement's bars to show.
const BLOCK_GAP = 'gap-x-2 gap-y-3'

const rowNumbersOf = (row: BlockYear, piece: BlockPiece) =>
  row.rows
    .slice(piece.firstRow, piece.lastRow + 1)
    .map(({ lane }) => laneNumberOf(lane))

const pieceGridRow = (row: BlockYear, piece: BlockPiece) =>
  `${gridRowOf(row, piece.span.track, piece.firstRow)} / ${gridRowOf(row, piece.span.track, piece.lastRow) + 1}`

// The first column names the account on each row, so the year sits above the rows instead.
export const BlockYearRows = ({
  row,
  choosing,
  openStatementId,
  highlight,
  statusFor,
  actions,
}: BlockYearRowsProps) => {
  const labelOf = (rows: number[]) =>
    rows.map((index) => row.rows[index]?.lane.label ?? '').join(' + ')

  return (
    <div>
      <div className="mb-2 text-xs font-medium tabular-nums text-foreground">
        {row.year}
      </div>
      <div
        className={cn('grid items-center', BLOCK_GAP)}
        style={{
          gridTemplateColumns: GRID,
          gridTemplateRows: `repeat(${row.slots.length}, ${TRACK_HEIGHT})`,
        }}
      >
        {row.slots.map((slot, index) => (
          <span
            key={`${slot.band}:${slot.row}`}
            className="text-[11px] text-ink-subtle"
            style={{ gridColumn: 1, gridRow: index + 1 }}
          >
            <AccountNumber
              numbers={row.rows[slot.row]?.lane.account?.numbers ?? []}
            />
          </span>
        ))}
        {row.rows.flatMap(({ lane, emptyCells }, index) =>
          emptyCells.map((cell) => (
            <MapCell
              key={`${lane.accountId}:${cell.monthIndex}`}
              cell={cell}
              gridRow={gridRowOf(row, 0, index)}
              laneLabel={lane.label}
              choosing={choosing}
              highlight={highlight}
              actions={actions}
            />
          )),
        )}
        {row.pieces.map((piece) => (
          <StitchedSpan
            key={`${piece.span.view.statement.id}:${piece.firstRow}`}
            span={piece.span}
            gridRow={pieceGridRow(row, piece)}
            rowNumbers={rowNumbersOf(row, piece)}
            laneLabel={labelOf(piece.coveredRows)}
            choosing={choosing}
            openStatementId={openStatementId}
            highlight={highlight}
            statusFor={statusFor}
            actions={actions}
          />
        ))}
      </div>
    </div>
  )
}
